import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, faClipboardList, faCheckCircle, faClock, faExclamationTriangle,
  faEye, faEdit, faCheck, faArrowRight, faExclamationCircle, faSearch, faFilter
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ui/ToastProvider';
import SupplierNavbar from '../../components/supplier/SupplierNavbar';
import { loadPortalDashboard } from '../../services/supplierApi';

interface Questionnaire {
  id: string;
  title: string;
  type: string;
  status: 'completed' | 'in_progress' | 'draft' | 'overdue' | 'sent';
  deadline: string;
  progress: number;
  accessToken: string;
  sentAt?: string;
  completedAt?: string;
  canAccess: boolean;
}

interface QuestionnaireStats {
  total: number;
  completed: number;
  pending: number;
  inProgress: number;
  overdue: number;
}

const SupplierQuestionnaires: React.FC = () => {
  const { masterToken } = useParams<{ masterToken: string }>();
  const navigate = useNavigate();
  const { showError } = useToast();

  // Core States
  const [loading, setLoading] = useState(true);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [supplierInfo, setSupplierInfo] = useState<any>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [stats, setStats] = useState<QuestionnaireStats>({
    total: 0,
    completed: 0,
    pending: 0,
    inProgress: 0,
    overdue: 0
  });

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    if (masterToken) {
      loadQuestionnaires();
    }
  }, [masterToken]);

  const loadQuestionnaires = async () => {
    try {
      setLoading(true);
      
      const storedSessionToken = sessionStorage.getItem('supplierPortalSession');
      
      if (!storedSessionToken) {
        navigate(`/supplier/portal/${masterToken}/login`);
        return;
      }

      const data = await loadPortalDashboard(masterToken!, storedSessionToken);
      
      const questionnairesWithAccess = data.questionnaires.map((q: any) => ({
        ...q,
        canAccess: (q as any).canAccess ?? true
      }));
      
      setQuestionnaires(questionnairesWithAccess);
      setSupplierInfo(data.supplier);
      setSessionToken(storedSessionToken);

      // Calculate stats
      const stats = {
        total: questionnairesWithAccess.length,
        completed: questionnairesWithAccess.filter((q: any) => q.status === 'completed').length,
        pending: questionnairesWithAccess.filter((q: any) => q.status === 'sent').length,
        inProgress: questionnairesWithAccess.filter((q: any) => q.status === 'in_progress').length,
        overdue: questionnairesWithAccess.filter((q: any) => q.status === 'overdue').length
      };
      setStats(stats);
      
    } catch (error) {
      console.error('Error loading questionnaires:', error);
      showError('Fragebögen konnten nicht geladen werden');
      
      if (error instanceof Error && error.message.includes('401')) {
        sessionStorage.removeItem('supplierPortalSession');
        navigate(`/supplier/portal/${masterToken}/login`);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter Functions
  const getFilteredQuestionnaires = () => {
    let filtered = [...questionnaires];
    
    if (searchTerm) {
      filtered = filtered.filter(q =>
        q.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(q => q.type === typeFilter);
    }
    
    return filtered;
  };

  // Status & Type Helper Functions
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Abgeschlossen';
      case 'in_progress': return 'In Bearbeitung';
      case 'overdue': return 'Überfällig';
      case 'sent': return 'Ausstehend';
      case 'draft': return 'Entwurf';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return faCheckCircle;
      case 'in_progress': return faClock;
      case 'overdue': return faExclamationTriangle;
      case 'sent': return faClipboardList;
      default: return faClipboardList;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'sent': return 'bg-orange-100 text-orange-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      producer: 'Produzent',
      trader: 'Händler',
      trader_producer: 'Händler & Produzent',
      non_food: 'Non-Food',
      gmo_declaration: 'GVO-Erklärung',
      conformity_declaration: 'Konformitätserklärung',
      emergency_contact: 'Notfallkontakt'
    };
    return types[type] || type;
  };

  const getTypeBadgeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      producer: 'bg-green-100 text-green-800',
      trader: 'bg-blue-100 text-blue-800',
      trader_producer: 'bg-purple-100 text-purple-800',
      non_food: 'bg-yellow-100 text-yellow-800',
      gmo_declaration: 'bg-orange-100 text-orange-800',
      conformity_declaration: 'bg-pink-100 text-pink-800',
      emergency_contact: 'bg-red-100 text-red-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getUniqueTypes = () => {
    const typeSet = new Set(questionnaires.map(q => q.type));
    const types = Array.from(typeSet);
    return types;
  };

  const handleOpenQuestionnaire = (questionnaire: Questionnaire) => {
    if (!questionnaire.canAccess) {
      showError('Dieser Fragebogen ist nicht verfügbar.');
      return;
    }

    console.log('📋 Opening questionnaire from questionnaire list:', questionnaire.id, questionnaire.accessToken);
    
    // **ERSTELLE SESSION TOKEN FÜR FRAGEBOGEN**
    const portalSessionToken = sessionStorage.getItem('supplierPortalSession');
    if (!portalSessionToken) {
      showError('Keine gültige Session gefunden. Bitte melden Sie sich erneut an.');
      navigate(`/supplier/portal/${masterToken}/login`);
      return;
    }
    
    // **SETZE FRAGEBOGEN SESSION TOKEN**
    // Der Fragebogen erwartet einen 'supplierSessionToken' und 'supplierSessionExpiry'
    const expiryTime = Date.now() + (24 * 60 * 60 * 1000); // 24 Stunden
    sessionStorage.setItem('supplierSessionToken', portalSessionToken);
    sessionStorage.setItem('supplierSessionExpiry', expiryTime.toString());
    sessionStorage.setItem('fromSupplierLogin', 'true');
    sessionStorage.setItem('supplierPortalMasterToken', masterToken!); // **WICHTIG: masterToken für Portal API speichern**
    
    // **NAVIGATE TO STANDARD QUESTIONNAIRE ROUTE**
    const questionnaireUrl = `/questionnaire/${questionnaire.accessToken}`;
    console.log('🧭 Navigating to questionnaire URL:', questionnaireUrl);
    console.log('🔐 Session token set for questionnaire access');
    console.log('🏠 Portal masterToken saved for API access:', masterToken);
    
    navigate(questionnaireUrl);
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SupplierNavbar 
          supplierName="Laden..." 
          masterToken={masterToken}
          pendingQuestionnaires={0}
          totalArticles={0}
        />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-6xl text-red-500 mb-4 animate-spin" />
            <p className="text-xl text-gray-600">Fragebögen werden geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  const filteredQuestionnaires = getFilteredQuestionnaires();

  return (
    <div className="min-h-screen bg-gray-50">
      <SupplierNavbar 
        supplierName={supplierInfo?.name || 'Lieferant'} 
        masterToken={masterToken}
        pendingQuestionnaires={stats.pending + stats.inProgress}
        totalArticles={0}
      />
      
      {/* Volle Breite Container wie im Dashboard */}
      <div className="w-full px-6 py-6">

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
        >
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Fragebögen durchsuchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="lg:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="all">Alle Status</option>
                <option value="completed">Abgeschlossen</option>
                <option value="in_progress">In Bearbeitung</option>
                <option value="sent">Ausstehend</option>
                <option value="overdue">Überfällig</option>
                <option value="draft">Entwurf</option>
              </select>
            </div>
            <div className="lg:w-48">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="all">Alle Typen</option>
                {getUniqueTypes().map(type => (
                  <option key={type} value={type}>{getTypeLabel(type)}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Questionnaires List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Ihre Fragebögen ({filteredQuestionnaires.length})
            </h2>
          </div>
          <div className="p-6">
            {filteredQuestionnaires.length > 0 ? (
              <div className="space-y-4">
                <AnimatePresence>
                  {filteredQuestionnaires.map((questionnaire, index) => {
                    const daysRemaining = Math.ceil((new Date(questionnaire.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                    
                    return (
                      <motion.div
                        key={questionnaire.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleOpenQuestionnaire(questionnaire)}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 cursor-pointer transition-all"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-4 mb-2">
                            <h3 className="font-medium text-gray-900 text-base">
                              {questionnaire.title}
                            </h3>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(questionnaire.status)}`}>
                              <FontAwesomeIcon icon={getStatusIcon(questionnaire.status)} className="mr-1" />
                              {getStatusLabel(questionnaire.status)}
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeBadgeColor(questionnaire.type)}`}>
                              {getTypeLabel(questionnaire.type)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span className={daysRemaining <= 2 ? 'text-red-600 font-medium' : ''}>
                              {daysRemaining > 0 ? `${daysRemaining} Tage verbleibend` : 'Überfällig'}
                            </span>
                            <span>•</span>
                            <span className="font-medium">
                              {questionnaire.progress}% abgeschlossen
                            </span>
                            {questionnaire.completedAt && (
                              <>
                                <span>•</span>
                                <span>
                                  Abgeschlossen am {new Date(questionnaire.completedAt).toLocaleDateString('de-DE')}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          {/* Progress Circle */}
                          <div className="relative w-12 h-12">
                            <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48">
                              <circle
                                cx="24"
                                cy="24"
                                r="18"
                                stroke="currentColor"
                                strokeWidth="3"
                                fill="transparent"
                                className="text-gray-200"
                              />
                              <circle
                                cx="24"
                                cy="24"
                                r="18"
                                stroke="currentColor"
                                strokeWidth="3"
                                fill="transparent"
                                strokeDasharray={`${2 * Math.PI * 18}`}
                                strokeDashoffset={`${2 * Math.PI * 18 * (1 - questionnaire.progress / 100)}`}
                                className={questionnaire.progress === 100 ? 'text-green-500' : 'text-blue-500'}
                                strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-xs font-semibold text-gray-700">
                                {questionnaire.progress}%
                              </span>
                            </div>
                          </div>
                          
                          <FontAwesomeIcon icon={faArrowRight} className="text-gray-400" />
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            ) : (
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faClipboardList} className="text-4xl text-gray-300 mb-3" />
                <p className="text-gray-600">
                  {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' 
                    ? 'Keine Fragebögen gefunden, die Ihren Filterkriterien entsprechen'
                    : 'Keine Fragebögen verfügbar'
                  }
                </p>
                {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                      setTypeFilter('all');
                    }}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Filter zurücksetzen
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SupplierQuestionnaires;